/**
 * 보관본(아카이브)과 현재 상품의 상세페이지 버전 비교 규칙.
 *
 * 화면에서 같은 판단을 두 군데가 한다 — 목록의 v1/v2 배지 색과 복원 확인 다이얼로그의 경고문.
 * 각자 비교식을 들고 있으면 한쪽만 바뀌었을 때 "목록은 같은 구성이라는데 다이얼로그는 다르다고
 * 경고한다"는 식으로 어긋난다. 같은 이유로 이 화면에서 `product-theme.ts` 가 먼저 빠져나왔다.
 */

/**
 * 보관본 버전을 모르면(`null`/`undefined`) 다르다고 보지 않는다.
 *
 * version 을 못 읽거나(JSON null/`'null'`/구버전 목록 응답) 없으면 다르다고 보지 않는다.
 * 그때 경고하면 멀쩡한 복원에도 노란 배지·복원 경고가 뜬다.
 */
export function isArchiveVersionMismatched(
  archivedVersion: number | null | undefined,
  currentVersion: number | null | undefined,
): boolean {
  return archivedVersion != null && archivedVersion !== currentVersion;
}

/**
 * 버전이 다른 보관본을 복원할 때 띄울 경고. 같거나 모르면 `null`.
 *
 * 이 스냅샷 경로의 v1은 보통 빈 배열이라 `updateOne`이 그대로 덮는다 —
 * v2 상품에 v1 보관본을 복원하면 요약·섹션이 전부 삭제되고 되돌릴 수 없다.
 */
export function getVersionMismatchWarning(
  archivedVersion: number | null | undefined,
  currentVersion: number | null | undefined,
): string | null {
  if (!isArchiveVersionMismatched(archivedVersion, currentVersion)) return null;
  if (archivedVersion === 1 && currentVersion === 2) {
    return '이 보관본은 리뉴얼 전(v1) 구성입니다.\n복원하면 지금 상세페이지(v2)의 요약·섹션이 모두 삭제됩니다.';
  }
  if (archivedVersion === 2 && currentVersion === 1) {
    return '이 보관본은 리뉴얼 후(v2) 구성입니다.\n복원하면 상세페이지가 요약·섹션 구성으로 바뀝니다.';
  }
  // v3 이후가 생기면 여기로 온다. 지우면 새 버전 조합이 조용히 무경고가 되므로 남겨 둔다.
  return `이 보관본은 상세페이지 v${archivedVersion} 구성입니다.\n복원하면 현재 v${currentVersion} 구성이 v${archivedVersion}로 바뀝니다.`;
}

/** 목록의 숫자, 상세 JSON 의 data.version, 구버전 응답의 빈 값을 한 갈래로 모은다. */
export function parseArchiveVersion(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * 화면 보관은 `data` 키를 넣고, 저장본 보관은 키 자체를 뺀다.
 * `data: undefined` 를 보내면 Nest 가 저장본 경로로 조용히 떨어진다.
 */
export function createArchiveRequestBody(name?: string, data?: unknown): { name?: string; data?: unknown } {
  return { name, ...(data ? { data } : {}) };
}
